<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex';
import MediaflowService from '@/services/MediaflowService';
import IconBase from '@/components/icon/IconBase.vue'
import FormButton from '../Atoms/FormButton.vue';
import FormInput from '../Atoms/FormInput.vue'
import IconCross from '../icon/IconCross.vue';
import IconTransform from '../icon/IconTransform.vue';
import IconMoveArrows from '../icon/IconMoveArrows.vue';
import { useNotification } from "@kyvg/vue3-notification"
import { transform } from 'lodash';

const store = useStore()
const { notify } = useNotification()

const props = defineProps({
    mediaflowId: {
      type: String,
      required: true
    },
    flowId: {
      type: String,
      required: true
    },
	media: {
		type: Object,
        required: true
	},
    topOffset: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    }
})
const emit = defineEmits({
  'position-changed': () => true
})

const root = ref(null)
const isVisible = ref(false)
const posObj = ref({
  positionX: 0,
  positionY: 0,
  scaleY: 100,
  scaleX: 100
})

const toggleVisible = () => {
	isVisible.value = !isVisible.value
}
const forceClose = (event) => {
  if (!root.value?.contains(event.target)) {
    isVisible.value = false
  }
}

onMounted(async () => {
  window.addEventListener('click', forceClose)
  if (props.media.transform) {
    posObj.value.positionX = props.media.transform.positionX
    posObj.value.positionY = props.media.transform.positionY
    posObj.value.scaleX = props.media.transform.scaleX
    posObj.value.scaleY = props.media.transform.scaleY
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('click', forceClose)
})

// Debounced function for updating transform on backend
const debouncedTransformUpdate = _.debounce(async (mediaflowId, flowId, mediaId, transform) => {
  try {
    const updateData = { transform };
    
    const updatedMedias = await MediaflowService.updateFlowMedia(
      mediaflowId,
      flowId,
      mediaId,
      updateData
    );

    if (Array.isArray(updatedMedias) && updatedMedias.length > 0) {
      const media = updatedMedias.find(item => item.id === mediaId);
      emit('media-updated', media)
    }
    
    notify({ group: 'success', text: 'Transform updated' });
  } catch (err) {
    console.error(err);
    notify({ group: 'error', text: 'Failed to update transform' });
  }
}, 500); // 500ms delay

const onTransformChange = (field, value) => {
  // Parse and validate the value
  let numValue = parseInt(value);
  if (isNaN(numValue)) numValue = 0;
  
  // Update local state
  posObj.value[field] = numValue;
  
  // Prepare transform data
  const transformData = {
    positionX: posObj.value.positionX,
    positionY: posObj.value.positionY,
    scaleX: posObj.value.scaleX,
    scaleY: posObj.value.scaleY
  };
  
  // Debounced update to backend
  debouncedTransformUpdate(
    props.mediaflowId,
    props.flowId,
    props.media.id,
    transformData
  );
}

</script>

<template>
	<div ref="root" :class="fullWidth ? 'block' : 'block md:inline-block'" class="relative">
		<FormButton :disabled="disabled" isIcon class="items-center justify-center saved-platform-custom-width" type="danger" @click="toggleVisible">
            <icon-base fill="none">
                <icon-move-arrows />
            </icon-base>
        </FormButton>

		<div class="fixed md:absolute w-full lg:w-auto max-w-[100vw] md:max-w-[480px] bottom-0 md:bottom-auto md:top-8 left-0 rounded-md bg-surface-4 p-3"
            :style="{left: '-80px', width: '220px', 'top': topOffset}"
			:class="isVisible ? '!block z-[99999999999] shadow-lg' : '!hidden'">
			<div class="flex items-center w-full -mt-2" >
			    <h4 class="text-sm text-white font-medium">Position</h4>
				<FormButton isIcon type="link" class="ml-auto" size="md" @click="toggleVisible">
					<icon-base :style="{'margin-left': '10px'}" fill="none">
						<icon-cross />
					</icon-base>
				</FormButton>

			</div>

            <div class="flex flex-col">
                <div class="flex items-center justify-center justify-between gap-x-2">
                    <div class="flex items-center flex-1">
                         <span class="text-sm text-white mr-2">X</span>
                        <div class="relative flex-1">
                          <input 
                            type="number" 
                            class="transform-input" 
                            :value="posObj.positionX"
                            @input="onTransformChange('positionX', $event.target.value)"
                            placeholder="0"
                          />
                          <span class="transform-unit">px</span>
                        </div>
                    </div>
                    <div class="flex items-center flex-1">
                         <span class="text-sm text-white mr-2">Y</span>
                        <div class="relative flex-1">
                          <input 
                            type="number" 
                            class="transform-input" 
                            :value="posObj.positionY"
                            @input="onTransformChange('positionY', $event.target.value)"
                            placeholder="0"
                          />
                          <span class="transform-unit">px</span>
                        </div>
                    </div>
                </div>

			    <h4 class="text-sm text-white font-medium mt-3 mb-1">Scale</h4>
                <div class="flex items-center justify-center justify-between gap-x-2">
                    <div class="flex items-center flex-1">
                         <span class="text-sm text-white mr-2">W</span>
                        <div class="relative flex-1">
                          <input 
                            type="number" 
                            class="transform-input" 
                            :value="posObj.scaleX"
                            min="0"
                            max="200"
                            @input="onTransformChange('scaleX', $event.target.value)"
                            placeholder="100"
                          />
                          <span class="transform-unit">%</span>
                        </div>
                    </div>
                    <div class="flex items-center flex-1">
                         <span class="text-sm text-white mr-2">H</span>
                        <div class="relative flex-1">
                          <input 
                            type="number" 
                            class="transform-input" 
                            :value="posObj.scaleY"
                            min="0"
                            max="200"
                            @input="onTransformChange('scaleY', $event.target.value)"
                            placeholder="100"
                          />
                          <span class="transform-unit">%</span>
                        </div>
                    </div>
                </div>
            </div>
		</div>
	</div>
</template>

<style scoped>
.list-wrapper {
	position: absolute;
	top: 40px;
	left: 0;
	z-index: 1010;
	background-color: var(--c-dark-4);
	border-radius: .5rem;
	border: 1px solid var(--c-neutral-n3);
	min-width: 300px;
}

.destination-list {
	max-height: 300px;
	min-width: 230px;
	overflow-y: auto;
	overflow-x: hidden;
}

.destination-list>li+li {
	border-top: 2px solid var(--c-dark-1)
}

.destination-list>li:first-child {
	border-top-left-radius: .5rem;
	border-top-right-radius: .5rem;
}

.destination-list>li:last-child {
	border-bottom-right-radius: .5rem;
	border-bottom-left-radius: .5rem;
}

.opacity-40 {
	opacity: .4;
}

.transform-input {
  width: 100%;
  height: 32px;
  padding: 4px 28px 4px 8px;
  background: var(--c-dark-3);
  border: 1px solid var(--c-dark-4);
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  outline: none;
  transition: all 0.2s ease;
}

.transform-input:hover {
  border-color: var(--c-dark-5);
  background: var(--c-dark-2);
}

.transform-input:focus {
  border-color: #5865f2;
  background: var(--c-dark-3);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
}

.transform-input::-webkit-inner-spin-button,
.transform-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.transform-input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.transform-unit {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--c-dark-6);
  font-size: 11px;
  font-weight: 500;
  pointer-events: none;
}

@media (max-width: 767px) {
	.destination-list {
		max-height: 150px;
	}
}

</style>